import React, { useState, useEffect } from "react";
import CloudBackground from "../community/shared/components/CloudBackground";
import PButton from "../../ui/input/PButton";
import {
  useGiftStripeCheckout,
  convertPersonalizationToCheckoutData,
} from "../../../api/gift_stripe";

type GiftPackage = {
  id: string;
  setupCount: number;
  totalPrice: number;
  pricePerSetup: number;
  sku: string;
  isPopular?: boolean;
};

type GiftPersonalizationData = {
  payerFullName: string;
  payerEmail: string;
  payerPhone: string;
  recipientFullName: string;
  recipientEmail: string;
  recipientPhone: string;
  recipientLocation: string;
  recipientAge: string;
  relationship: string;
  personalizedMessage: string;
};

type ValidationErrors = {
  payerFullName?: string;
  payerEmail?: string;
  payerPhone?: string;
  recipientFullName?: string;
  recipientEmail?: string;
  recipientPhone?: string;
  recipientLocation?: string;
  recipientAge?: string;
  relationship?: string;
  personalizedMessage?: string;
};

type GiftPersonalizationFormProps = {
  selectedPackage: GiftPackage;
  onSubmit?: (personalizationData: GiftPersonalizationData) => void;
  onBackClick?: () => void;
  giftPurchaseUrl?: string;
  isLoading?: boolean;
  activeStep?: 1 | 2;
  onStepChange?: (step: 1 | 2) => void;
  onTooYoungInfoClick?: () => void;
  onUnsupportedLocationInfoClick?: () => void;
};

const GiftPersonalizationForm: React.FC<GiftPersonalizationFormProps> = ({
  selectedPackage,
  onSubmit,
  onBackClick,
  giftPurchaseUrl,
  isLoading = false,
  activeStep = 1,
  onStepChange,
  onTooYoungInfoClick,
  onUnsupportedLocationInfoClick,
}) => {
  const [formData, setFormData] = useState<GiftPersonalizationData>({
    payerFullName: "",
    payerEmail: "",
    payerPhone: "",
    recipientFullName: "",
    recipientEmail: "",
    recipientPhone: "",
    recipientLocation: "",
    recipientAge: "",
    relationship: "",
    personalizedMessage: "",
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [stepHistory, setStepHistory] = useState<{
    step1Data: Partial<GiftPersonalizationData>;
    step2Data: Partial<GiftPersonalizationData>;
    lastCompletedStep: 0 | 1 | 2;
  }>({
    step1Data: {},
    step2Data: {},
    lastCompletedStep: 0,
  });
  const { initiateGiftCheckout } = useGiftStripeCheckout();

  // Helper functions for step data management
  const saveStepData = (
    step: 1 | 2,
    data: Partial<GiftPersonalizationData>,
  ) => {
    setStepHistory((prev) => ({
      ...prev,
      [`step${step}Data`]: data,
      lastCompletedStep: step,
    }));
  };

  const restoreStepData = (step: 1 | 2) => {
    const stepData = stepHistory[
      `step${step}Data` as keyof typeof stepHistory
    ] as Partial<GiftPersonalizationData>;
    if (stepData && Object.keys(stepData).length > 0) {
      setFormData((prev) => ({ ...prev, ...stepData }));
    }
  };

  const clearFormHistory = () => {
    setStepHistory({
      step1Data: {},
      step2Data: {},
      lastCompletedStep: 0,
    });
    try {
      localStorage.removeItem("giftPersonalizationForm");
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  };

  // Save and restore form data functionality
  useEffect(() => {
    // Try to restore from localStorage on component mount
    try {
      const savedData = localStorage.getItem("giftPersonalizationForm");
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        if (parsedData.formData) {
          setFormData(parsedData.formData);
        }
        if (parsedData.stepHistory) {
          setStepHistory(parsedData.stepHistory);
        }
      }
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  }, []);

  // Save to localStorage whenever formData or stepHistory changes
  useEffect(() => {
    try {
      localStorage.setItem(
        "giftPersonalizationForm",
        JSON.stringify({
          formData,
          stepHistory,
          timestamp: Date.now(),
        }),
      );
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  }, [formData, stepHistory]);

  // Handle step changes and restore data accordingly
  useEffect(() => {
    if (activeStep === 1 || activeStep === 2) {
      restoreStepData(activeStep);
    }
  }, [activeStep]);

  // Add fancy fonts safely
  useEffect(() => {
    // Add custom fonts - matching GiftLandingPage approach exactly
    const preloadLink = document.createElement("link");
    preloadLink.rel = "preload";
    preloadLink.href = "/assets/fonts/SeriouslyNostalgicFn-Regular.otf";
    preloadLink.as = "font";
    preloadLink.type = "font/otf";
    preloadLink.crossOrigin = "anonymous";
    document.head.appendChild(preloadLink);

    const style = document.createElement("style");
    style.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,500;1,600;1,700;1,800;1,900&display=swap');
      
      @font-face {
        font-family: 'Seriously Nostalgic';
        src: url('/assets/fonts/SeriouslyNostalgicFn-Regular.otf') format('opentype');
        font-display: swap;
        font-weight: normal;
        font-style: normal;
      }
      
      .font-playfair { font-family: 'Playfair Display', serif; }
      .font-seriously-nostalgic { 
        font-family: 'Seriously Nostalgic', 'Playfair Display', 'Times New Roman', serif !important;
        font-weight: normal;
        font-style: normal;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
      if (document.head.contains(preloadLink)) {
        document.head.removeChild(preloadLink);
      }
    };
  }, []);

  const relationshipOptions = [
    "Friend",
    "Sister",
    "Brother",
    "Family member",
    "Colleague",
    "Roommate",
    "Ex (but we're cool)",
    "Other",
  ];

  const handleInputChange =
    (field: keyof GiftPersonalizationData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = e.target;
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error for this field when user starts typing
      if (errors[field as keyof ValidationErrors]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const handleSelectChange =
    (field: keyof GiftPersonalizationData) => (value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error for this field when user selects
      if (errors[field as keyof ValidationErrors]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const baseInputClass =
    "w-full backdrop-blur-md border border-white/25 rounded-xl px-4 py-3 text-base font-medium shadow-lg outline-none text-white placeholder-white/60 focus:ring-2 focus:ring-white/40 focus:border-white/50 focus:bg-white/20";

  const frostedFilledStyle = {
    backgroundColor: "rgba(255, 255, 255, 0.12)",
    color: "#FFFFFF",
  };

  const renderStepOneFields = () => (
    <div className="space-y-4">
      <div>
        <input
          type="text"
          value={formData.recipientFullName}
          onChange={handleInputChange("recipientFullName")}
          placeholder="Recipient’s full name"
          disabled={isLoading || isProcessing}
          className={baseInputClass}
          style={frostedFilledStyle}
        />
        {errors.recipientFullName && (
          <div className="mt-2 text-red-300 text-sm">
            {errors.recipientFullName}
          </div>
        )}
      </div>

      <div>
        <input
          type="number"
          value={formData.recipientAge}
          onChange={handleInputChange("recipientAge")}
          placeholder="Recipient's age"
          disabled={isLoading || isProcessing}
          className={baseInputClass}
          style={frostedFilledStyle}
          min="22"
          max="100"
        />
        {errors.recipientAge && (
          <div className="mt-2 text-red-300 text-sm">{errors.recipientAge}</div>
        )}
        <div className="mt-2 text-right">
          <button
            type="button"
            onClick={onTooYoungInfoClick}
            className="text-white/70 hover:text-white text-sm underline transition-colors bg-transparent border-none cursor-pointer"
          >
            Why is there an age requirement?
          </button>
        </div>
      </div>

      <div>
        <select
          value={formData.recipientLocation}
          onChange={(e) =>
            handleSelectChange("recipientLocation")(e.target.value)
          }
          disabled={isLoading || isProcessing}
          className={`${baseInputClass} appearance-none cursor-pointer`}
          style={{
            ...frostedFilledStyle,
            backgroundImage:
              "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23FFFFFF' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e\")",
            backgroundPosition: "right 1rem center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "1.25em 1.25em",
          }}
        >
          <option value="" disabled className="text-blue-900">
            Select recipient’s location
          </option>
          <option value="New York, NY" className="text-blue-900">
            New York, NY
          </option>
          <option value="San Francisco, CA" className="text-blue-900">
            San Francisco, CA
          </option>
          <option value="Los Angeles, CA" className="text-blue-900">
            Los Angeles, CA
          </option>
          <option value="Chicago, IL" className="text-blue-900">
            Chicago, IL
          </option>
        </select>
        {errors.recipientLocation && (
          <div className="mt-2 text-red-300 text-sm">
            {errors.recipientLocation}
          </div>
        )}
        <div className="mt-2 text-right">
          <button
            type="button"
            onClick={onUnsupportedLocationInfoClick}
            className="text-white/70 hover:text-white text-sm underline transition-colors bg-transparent border-none cursor-pointer"
          >
            Don't see your location?
          </button>
        </div>
      </div>
    </div>
  );

  const renderStepTwoFields = () => (
    <div className="space-y-4">
      <div>
        <input
          type="text"
          value={formData.payerFullName}
          onChange={handleInputChange("payerFullName")}
          placeholder="Your full name"
          disabled={isLoading || isProcessing}
          className={baseInputClass}
          style={frostedFilledStyle}
        />
        {errors.payerFullName && (
          <div className="mt-2 text-red-300 text-sm">
            {errors.payerFullName}
          </div>
        )}
      </div>

      <div>
        <div className="flex gap-3">
          <div className="w-20 flex-shrink-0">
            <button
              type="button"
              className="w-full flex items-center justify-center border border-white/20 rounded-xl px-2 py-3 text-white hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/30 cursor-pointer transition-all"
              style={frostedFilledStyle}
              disabled={isLoading || isProcessing}
            >
              <span className="mr-1 text-sm">🇺🇸</span>
              <span className="text-sm">+1</span>
              <svg
                className="w-3 h-3 ml-1 text-white/70"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
          <div className="flex-1">
            <input
              type="tel"
              value={formData.payerPhone}
              onChange={(e) => {
                const { value } = e.target;
                setFormData((prev) => ({ ...prev, payerPhone: value }));
                if (errors.payerPhone) {
                  setErrors((prev) => ({ ...prev, payerPhone: undefined }));
                }
              }}
              placeholder="Your mobile number"
              disabled={isLoading || isProcessing}
              className={baseInputClass}
              style={frostedFilledStyle}
              autoComplete="tel"
            />
          </div>
        </div>
        {errors.payerPhone && (
          <div className="mt-2 text-red-300 text-sm">{errors.payerPhone}</div>
        )}
      </div>

      <div>
        <input
          type="email"
          value={formData.payerEmail}
          onChange={handleInputChange("payerEmail")}
          placeholder="Your email"
          disabled={isLoading || isProcessing}
          className={baseInputClass}
          style={frostedFilledStyle}
        />
        {errors.payerEmail && (
          <div className="mt-2 text-red-300 text-sm">{errors.payerEmail}</div>
        )}
      </div>

      <div>
        <input
          type="email"
          value={formData.recipientEmail}
          onChange={handleInputChange("recipientEmail")}
          placeholder="Recipient's email"
          disabled={isLoading || isProcessing}
          className={baseInputClass}
          style={frostedFilledStyle}
        />
        {errors.recipientEmail && (
          <div className="mt-2 text-red-300 text-sm">
            {errors.recipientEmail}
          </div>
        )}
      </div>

      <div>
        <div className="flex gap-3">
          <div className="w-20 flex-shrink-0">
            <button
              type="button"
              className="w-full flex items-center justify-center border border-white/20 rounded-xl px-2 py-3 text-white hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/30 cursor-pointer transition-all"
              style={frostedFilledStyle}
              disabled={isLoading || isProcessing}
            >
              <span className="mr-1 text-sm">🇺🇸</span>
              <span className="text-sm">+1</span>
              <svg
                className="w-3 h-3 ml-1 text-white/70"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
          <div className="flex-1">
            <input
              type="tel"
              value={formData.recipientPhone}
              onChange={(e) => {
                const { value } = e.target;
                setFormData((prev) => ({ ...prev, recipientPhone: value }));
                if (errors.recipientPhone) {
                  setErrors((prev) => ({
                    ...prev,
                    recipientPhone: undefined,
                  }));
                }
              }}
              placeholder="Recipient’s mobile number"
              disabled={isLoading || isProcessing}
              className={baseInputClass}
              style={frostedFilledStyle}
              autoComplete="tel"
            />
          </div>
        </div>
        {errors.recipientPhone && (
          <div className="mt-2 text-red-300 text-sm">
            {errors.recipientPhone}
          </div>
        )}
      </div>

      <div>
        <select
          value={formData.relationship}
          onChange={(e) => handleSelectChange("relationship")(e.target.value)}
          disabled={isLoading || isProcessing}
          className={`${baseInputClass} appearance-none cursor-pointer`}
          style={{
            ...frostedFilledStyle,
            backgroundImage:
              "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23FFFFFF' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e\")",
            backgroundPosition: "right 1.5rem center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "1.5em 1.5em",
          }}
        >
          <option value="" disabled className="text-blue-900">
            How do you know them?
          </option>
          {relationshipOptions.map((option) => (
            <option key={option} value={option} className="text-blue-900">
              {option}
            </option>
          ))}
        </select>
        {errors.relationship && (
          <div className="mt-2 text-red-300 text-sm">{errors.relationship}</div>
        )}
      </div>

      <div>
        <div className="relative">
          <textarea
            value={formData.personalizedMessage}
            onChange={handleInputChange("personalizedMessage")}
            placeholder="Enter your personalised message..."
            disabled={isLoading || isProcessing}
            maxLength={160}
            rows={4}
            className="w-full backdrop-blur-md border border-white/25 rounded-xl px-4 py-3 text-base font-medium shadow-lg outline-none text-white placeholder-white/60 focus:ring-2 focus:ring-white/40 focus:border-white/50 focus:bg-white/20 resize-none"
            style={frostedFilledStyle}
          />
          <div className="absolute bottom-3 right-4 text-white/70 text-xs font-medium">
            {formData.personalizedMessage.length}/160
          </div>
        </div>
        {errors.personalizedMessage && (
          <div className="mt-2 text-red-300 text-sm">
            {errors.personalizedMessage}
          </div>
        )}
      </div>
    </div>
  );

  const validateForm = (step: 1 | 2): boolean => {
    const newErrors: ValidationErrors = {};

    if (step === 1 || step === 2) {
      if (!formData.recipientFullName.trim()) {
        newErrors.recipientFullName = "Recipient's full name is required";
      }

      const age = parseInt(formData.recipientAge, 10);
      if (!formData.recipientAge.trim()) {
        newErrors.recipientAge = "Recipient's age is required";
      } else if (Number.isNaN(age) || age < 22 || age > 100) {
        newErrors.recipientAge = "Age must be between 22 and 100";
      }

      if (!formData.recipientLocation.trim()) {
        newErrors.recipientLocation = "Recipient's location is required";
      }
    }

    if (step === 2) {
      if (!formData.payerFullName.trim()) {
        newErrors.payerFullName = "Your full name is required";
      }

      if (!formData.payerEmail.trim()) {
        newErrors.payerEmail = "Your email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.payerEmail)) {
        newErrors.payerEmail = "Please enter a valid email address";
      }

      const payerPhoneTrimmed = formData.payerPhone.trim();
      if (!payerPhoneTrimmed) {
        newErrors.payerPhone = "Your mobile number is required";
      } else if (payerPhoneTrimmed.length < 3) {
        newErrors.payerPhone = "Please enter a valid phone number";
      }

      if (!formData.recipientEmail.trim()) {
        newErrors.recipientEmail = "Recipient's email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.recipientEmail)) {
        newErrors.recipientEmail = "Please enter a valid email address";
      }

      const recipientPhoneTrimmed = formData.recipientPhone.trim();
      if (!recipientPhoneTrimmed) {
        newErrors.recipientPhone = "Recipient's mobile number is required";
      } else if (recipientPhoneTrimmed.length < 3) {
        newErrors.recipientPhone = "Please enter a valid phone number";
      }

      if (!formData.relationship.trim()) {
        newErrors.relationship = "Please select how you know them";
      }

      if (formData.personalizedMessage.length > 160) {
        newErrors.personalizedMessage = "Message cannot exceed 160 characters";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStepOneContinue = () => {
    if (!validateForm(1)) {
      return;
    }

    // Save current step 1 data before moving to step 2
    saveStepData(1, {
      recipientFullName: formData.recipientFullName,
      recipientAge: formData.recipientAge,
      recipientLocation: formData.recipientLocation,
    });

    if (onStepChange) {
      onStepChange(2);
      // Restore step 2 data if it exists
      setTimeout(() => restoreStepData(2), 0);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm(2)) {
      return;
    }

    // If giftPurchaseUrl is provided, use traditional form submission
    if (giftPurchaseUrl) {
      // Clear form history on successful submission
      clearFormHistory();
      onSubmit?.(formData);
      return;
    }

    // Otherwise, use Stripe checkout
    setIsProcessing(true);

    try {
      const checkoutData = convertPersonalizationToCheckoutData(
        {
          sku: selectedPackage.sku,
          totalPrice: selectedPackage.totalPrice,
        },
        formData,
      );

      await initiateGiftCheckout(checkoutData);

      // If we reach here, the checkout was successful
      // Clear form history on successful submission
      clearFormHistory();
      // Stripe checkout will redirect, so we don't need to do anything else
      onSubmit?.(formData);
    } catch (error) {
      setErrors({
        ...errors,
        payerEmail: `Something went wrong: ${error instanceof Error ? error.message : "Unknown error"}. Please try again.`,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <CloudBackground>
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-10">
        {(onBackClick || onStepChange) && (
          <div className="absolute top-8 left-8">
            <PButton
              onClick={() => {
                if (activeStep === 2 && onStepChange) {
                  // Save current step 2 data before going back to step 1
                  saveStepData(2, {
                    payerFullName: formData.payerFullName,
                    payerEmail: formData.payerEmail,
                    payerPhone: formData.payerPhone,
                    recipientEmail: formData.recipientEmail,
                    recipientPhone: formData.recipientPhone,
                    relationship: formData.relationship,
                    personalizedMessage: formData.personalizedMessage,
                  });

                  onStepChange(1);
                  // Restore step 1 data
                  setTimeout(() => restoreStepData(1), 0);
                } else {
                  onBackClick?.();
                }
              }}
              kind="secondary"
              className="bg-white/20 backdrop-blur-md border border-white/40 text-white hover:bg-white/30 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm rounded-full shadow-lg transition-all"
            >
              ← Back
            </PButton>
          </div>
        )}

        <div className="w-full max-w-md mx-auto">
          <div className="text-center mb-8">
            <h1
              className="font-seriously-nostalgic text-3xl sm:text-4xl md:text-[44px] font-semibold text-white"
              style={{ letterSpacing: "-0.02em" }}
            >
              {activeStep === 1
                ? "Tell us who we're setting up"
                : "Great! Let's personalize the gift"}
            </h1>
          </div>

          {activeStep === 1 ? (
            <>
              <div className="space-y-4">{renderStepOneFields()}</div>
              <div className="pt-6">
                <PButton
                  onClick={handleStepOneContinue}
                  kind="primary"
                  disabled={isLoading || isProcessing}
                  className="w-full !bg-black hover:!bg-[#111111] !text-white !border-black px-6 py-3 text-base font-medium rounded-full shadow-lg outline-0 ring-0 focus:outline-0 focus:ring-0 !outline-0 !ring-0"
                >
                  Continue
                </PButton>
              </div>
            </>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-4">{renderStepTwoFields()}</div>

              <div className="pt-6">
                {giftPurchaseUrl ? (
                  <PButton
                    href={giftPurchaseUrl}
                    kind="primary"
                    disabled={isLoading || isProcessing}
                    className="w-full !bg-black hover:!bg-[#111111] !text-white !border-black px-6 py-3 text-base font-medium rounded-full shadow-lg outline-0 ring-0 focus:outline-0 focus:ring-0 !outline-0 !ring-0"
                  >
                    {isLoading || isProcessing
                      ? "Processing..."
                      : "Gift setups"}
                  </PButton>
                ) : (
                  <PButton
                    type="submit"
                    kind="primary"
                    disabled={isLoading || isProcessing}
                    className="w-full !bg-black hover:!bg-[#111111] !text-white !border-black px-6 py-3 text-base font-medium rounded-full shadow-lg outline-0 ring-0 focus:outline-0 focus:ring-0 !outline-0 !ring-0"
                  >
                    {(() => {
                      if (isProcessing) return "Redirecting to checkout...";
                      if (isLoading) return "Processing...";
                      return "Gift setups";
                    })()}
                  </PButton>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </CloudBackground>
  );
};

GiftPersonalizationForm.defaultProps = {
  onSubmit: undefined,
  onBackClick: undefined,
  giftPurchaseUrl: undefined,
  isLoading: false,
  activeStep: 1,
  onStepChange: undefined,
  onTooYoungInfoClick: undefined,
  onUnsupportedLocationInfoClick: undefined,
};

export default GiftPersonalizationForm;
